// "New Hetzner server" — prepare a fresh Hetzner dedicated server (booted into
// the rescue system) as a bot host straight from the dashboard:
//   NewHetznerServerModal   rescue login → hardware/disk check → confirm → start
//   BootstrapProgressModal  live phases + log; recovery steps + Resume on failure
// Backend: POST /servers/bootstrap*, helpers/host-bootstrap.js, host-bootstrap.sh.
import { useState, useEffect, useRef } from 'react'
import { X, Loader2, CheckCircle2, Circle, AlertCircle, AlertTriangle, Search, Rocket, RotateCw, Cpu, HardDrive, MemoryStick } from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'

const input = 'tw-w-full tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-input tw-px-3 tw-py-2 tw-text-sm tw-text-fg tw-box-border'
const label = 'tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-muted'
const hint = 'tw-mt-1 tw-text-xs tw-text-muted'
const errBox = 'tw-rounded-md tw-border tw-border-solid tw-border-red-500/40 tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300'

function Modal({ title, onClose, children, footer, wide }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ maxWidth: wide ? 820 : 620 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body tw-flex tw-flex-col tw-gap-4">{children}</div>
                {footer && <div className="modal-footer tw-flex tw-justify-end tw-gap-2">{footer}</div>}
            </div>
        </div>
    )
}

// ------------------------------------------------------------------ start

export function NewHetznerServerModal({ onClose, onStarted }) {
    const [form, setForm] = useState({ name: '', host_ip: '', rescue_password: '', root_password: '' })
    const [check, setCheck] = useState(null)       // precheck result
    const [checking, setChecking] = useState(false)
    const [confirmIp, setConfirmIp] = useState('')
    const [wipe, setWipe] = useState(false)
    const [starting, setStarting] = useState(false)
    const [err, setErr] = useState(null)
    const patch = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (k === 'host_ip' || k === 'rescue_password') setCheck(null) }

    async function runCheck() {
        setErr(null); setChecking(true); setCheck(null)
        try {
            const r = await audiosocketBotsApi.bootstrapPrecheck({ host_ip: form.host_ip.trim(), rescue_password: form.rescue_password })
            if (!r.data.success) setErr(r.data.error)
            else setCheck(r.data)
        } catch (e) { setErr(e.response?.data?.error || e.message) }
        finally { setChecking(false) }
    }

    async function start() {
        setErr(null); setStarting(true)
        try {
            const r = await audiosocketBotsApi.startBootstrap({
                name: form.name.trim(), host_ip: form.host_ip.trim(), rescue_password: form.rescue_password,
                root_password: form.root_password || undefined, confirm_ip: confirmIp.trim(), wipe
            })
            onStarted(r.data.server_id)
        } catch (e) { setErr(e.response?.data?.error || e.message); setStarting(false) }
    }

    const info = check?.info
    const ready = info && confirmIp.trim() === form.host_ip.trim() && (!info.has_data || wipe) && form.name.trim()
        && (!form.root_password || form.root_password.length >= 16)

    return (
        <Modal title="New Hetzner server" onClose={onClose}
            footer={<>
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                {!info
                    ? <button className="btn btn-primary" onClick={runCheck}
                        disabled={checking || !form.host_ip.trim() || !form.rescue_password}>
                        {checking ? <Loader2 size={14} className="spinning" /> : <Search size={14} />} Check server
                      </button>
                    : <button className="btn btn-primary" onClick={start} disabled={!ready || starting}>
                        {starting ? <Loader2 size={14} className="spinning" /> : <Rocket size={14} />} Install &amp; set up
                      </button>}
            </>}>
            <div className="tw-text-sm tw-text-muted">
                For a new Hetzner dedicated server that is booted into the <strong>rescue system</strong> (a new
                server arrives that way — the root password is in the order email). The dashboard installs
                Debian 12 + Proxmox, the bot network, Asterisk and the golden template. Takes about 30–40 minutes.
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
                <div>
                    <label className={label}>Name *</label>
                    <input className={input} value={form.name} onChange={e => patch('name', e.target.value)} placeholder="ace-solution-2" />
                </div>
                <div>
                    <label className={label}>Server IP *</label>
                    <input className={input} value={form.host_ip} onChange={e => patch('host_ip', e.target.value)} placeholder="95.217.224.222" />
                </div>
                <div>
                    <label className={label}>Rescue root password *</label>
                    <input className={input} type="password" autoComplete="off" value={form.rescue_password}
                        onChange={e => patch('rescue_password', e.target.value)} />
                    <div className={hint}>Used once to install the dashboard's SSH key — not stored.</div>
                </div>
                <div>
                    <label className={label}>Proxmox root password (optional)</label>
                    <input className={input} type="password" autoComplete="new-password" value={form.root_password}
                        onChange={e => patch('root_password', e.target.value)} placeholder="random if empty" />
                    <div className={hint}>For the Proxmox web UI (port 8006). Min 16 characters.</div>
                </div>
            </div>

            {err && <div className={errBox}>{err}</div>}

            {info && (
                <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-green-400">
                        <CheckCircle2 size={16} /> Rescue system reachable
                    </div>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-2 tw-text-sm sm:tw-grid-cols-3">
                        <span className="tw-flex tw-items-center tw-gap-1.5"><Cpu size={14} className="tw-text-muted" /> {info.cpu} ({info.threads} threads)</span>
                        <span className="tw-flex tw-items-center tw-gap-1.5"><MemoryStick size={14} className="tw-text-muted" /> {info.mem_gb} GB RAM</span>
                        <span className="tw-flex tw-items-center tw-gap-1.5"><HardDrive size={14} className="tw-text-muted" />
                            {info.disks.length >= 2 && info.disks[0].size_gb === info.disks[1].size_gb ? 'RAID 1 on 2 disks' : 'single disk'}</span>
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-font-mono tw-text-xs">
                        {info.disks.map(d => (
                            <div key={d.name} className={d.partitions || d.filesystems ? 'tw-text-amber-300' : 'tw-text-muted'}>
                                {d.name} · {d.size_gb} GB · {d.model}{d.partitions || d.filesystems ? ` · ${d.partitions} partitions — HAS DATA` : ' · empty'}
                            </div>
                        ))}
                    </div>
                    {info.threads && (
                        <div className={hint}>Fits roughly {Math.max(1, info.threads - 1)} bots (about one CPU thread each).</div>
                    )}
                    {info.has_data && (
                        <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-rounded-md tw-bg-amber-500/10 tw-p-3 tw-text-sm tw-text-amber-200">
                            <input type="checkbox" checked={wipe} onChange={e => setWipe(e.target.checked)} className="tw-mt-0.5" />
                            <span><strong>The disks are not empty.</strong> Tick to wipe them and install anyway — everything on this server is erased.</span>
                        </label>
                    )}
                    {check.existing && (
                        <div className="tw-text-xs tw-text-muted">Already listed as “{check.existing.name}” (0 bots) — that entry will be reused.</div>
                    )}
                    <div>
                        <label className={label}>Type the server IP to confirm the install</label>
                        <input className={input} value={confirmIp} onChange={e => setConfirmIp(e.target.value)} placeholder={form.host_ip} />
                    </div>
                </div>
            )}
        </Modal>
    )
}

// ------------------------------------------------------------------ progress

const ACTIVE = ['running']
const STATUS_TEXT = {
    running: 'Setting up…', done: 'Ready', failed: 'Failed',
    needs_attention: 'Needs your help', interrupted: 'Interrupted'
}

export function BootstrapProgressModal({ serverId, onClose }) {
    const [job, setJob] = useState(null)
    const [err, setErr] = useState(null)
    const [rescuePw, setRescuePw] = useState('')
    const [reinstall, setReinstall] = useState(false)
    const [resuming, setResuming] = useState(false)
    const logRef = useRef(null)

    async function load() {
        try {
            const r = await audiosocketBotsApi.getBootstrap(serverId)
            setJob(r.data.job)
        } catch (e) { setErr(e.response?.data?.error || e.message) }
    }
    useEffect(() => {
        load()
        const t = setInterval(load, 3000)
        return () => clearInterval(t)
    }, [serverId])
    useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight }, [job?.log_tail])

    async function resume() {
        setErr(null); setResuming(true)
        try {
            await audiosocketBotsApi.resumeBootstrap(serverId, { rescue_password: rescuePw || undefined, reinstall })
            setRescuePw(''); setReinstall(false)
            await load()
        } catch (e) { setErr(e.response?.data?.error || e.message) }
        finally { setResuming(false) }
    }

    const phases = job?.phases || []
    const doneIdx = job?.done_phase ? phases.findIndex(p => p.key === job.done_phase) : -1
    const running = ACTIVE.includes(job?.status)
    const stuck = job && ['failed', 'needs_attention', 'interrupted'].includes(job.status)

    return (
        <Modal wide title={`Setting up ${job?.server_name || 'server'}${job?.host_ip ? ` — ${job.host_ip}` : ''}`} onClose={onClose}
            footer={<button className="btn btn-secondary" onClick={onClose}>{running ? 'Close (keeps running)' : 'Close'}</button>}>
            {!job ? <div className="tw-flex tw-justify-center tw-p-6"><Loader2 className="spinning" /></div> : (<>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-text-sm tw-font-semibold">
                        {STATUS_TEXT[job.status] || job.status}
                        {running && job.step && <span className="tw-ml-2 tw-font-normal tw-text-muted">{job.step}</span>}
                    </div>
                    {job.status === 'done' && <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-green-400"><CheckCircle2 size={16} /> Ready for bots</span>}
                </div>

                <div className="tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-1.5 sm:tw-grid-cols-2">
                    {phases.map((p, i) => {
                        const isDone = i <= doneIdx
                        const isCurrent = !isDone && i === doneIdx + 1
                        return (
                            <div key={p.key} className={`tw-flex tw-items-center tw-gap-2 tw-text-sm ${isDone ? 'tw-text-fg' : 'tw-text-muted'}`}>
                                {isDone ? <CheckCircle2 size={15} className="tw-shrink-0 tw-text-green-400" />
                                    : isCurrent && running ? <Loader2 size={15} className="spinning tw-shrink-0 tw-text-blue-400" />
                                    : isCurrent && stuck ? <AlertCircle size={15} className="tw-shrink-0 tw-text-red-400" />
                                    : <Circle size={15} className="tw-shrink-0 tw-opacity-40" />}
                                {p.label}
                            </div>
                        )
                    })}
                </div>

                <pre ref={logRef} className="tw-m-0 tw-max-h-72 tw-overflow-auto tw-whitespace-pre-wrap tw-rounded-md tw-bg-black tw-p-3 tw-font-mono tw-text-[11px] tw-text-green-300">
                    {job.log_tail || '(waiting for output…)'}
                </pre>

                {stuck && (
                    <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-500/40 tw-bg-amber-500/10 tw-p-4">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-amber-200">
                            <AlertTriangle size={16} /> {job.error || STATUS_TEXT[job.status]}
                        </div>
                        {job.recovery && <pre className="tw-m-0 tw-whitespace-pre-wrap tw-font-sans tw-text-sm tw-text-fg-secondary">{job.recovery}</pre>}
                        <div className="tw-grid tw-grid-cols-1 tw-items-end tw-gap-3 sm:tw-grid-cols-[1fr_auto]">
                            <div>
                                <label className={label}>Rescue password (only if the server is in rescue now)</label>
                                <input className={input} type="password" autoComplete="off" value={rescuePw} onChange={e => setRescuePw(e.target.value)} />
                            </div>
                            <button className="btn btn-primary" onClick={resume} disabled={resuming}>
                                {resuming ? <Loader2 size={14} className="spinning" /> : <RotateCw size={14} />} Resume
                            </button>
                        </div>
                        {rescuePw && doneIdx >= 1 && (
                            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
                                <input type="checkbox" checked={reinstall} onChange={e => setReinstall(e.target.checked)} />
                                Reinstall from scratch (the server is in rescue, so the previous install is wiped)
                            </label>
                        )}
                    </div>
                )}
                {err && <div className={errBox}>{err}</div>}
            </>)}
        </Modal>
    )
}
